import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Checkbox,
  FormControlLabel,
  Input,
  CardMedia,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  useAddCategoryMutation,
  useDeleteCategoryMutation,
  useLazyGetCategoryByIdQuery,
  useLazyGetCategoryQuery,
  useUpdateCategoryMutation,
} from "../../service/category";
import { showError, showToast } from "../../constants/toasts";
import { Category } from "../../types/General";
import { useFormik } from "formik";
import * as Yup from "yup";
import { UploadMedia } from "../../utils/uploadMedia";
import SwitchToggle from "../../components/SwitchToggle";
import { isValidInput } from "../../utils/validations";
import Pagination from "../../components/Panigation";
import usePermission from "../../hooks/usePermission";
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

const subcategoryValidationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be at most 100 characters"),
  description: Yup.string().max(
    500,
    "Description must be at most 500 characters"
  ),
  image: Yup.string().required("Image is required"),
  isFeatured: Yup.boolean(),
});

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
const ViewPromotion = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [openUserModal, setOpenUserModal] = React.useState(false);
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
  const [category, setCategory] = React.useState<Category>();
  const [getCategoryById, { isLoading: isLoadingCategory }] =
    useLazyGetCategoryByIdQuery();

  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  useEffect(() => {
    if (valueTabs === 0 && id) {
      fetchCategoryData();
    }
  }, [id, valueTabs]);

  const fetchCategoryData = async () => {
    try {
      const response = await getCategoryById(id as string).unwrap();
      if (response.code === 200) {
        const category = response?.data;
        setCategory(category);
      }
    } catch (error: any) {
      console.error("Error fetching category data:", error);
      setCategory(undefined);
      showError(error?.data?.message || "Failed to fetch category data");
      navigate("/manage-categories");
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Product Category</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-categories")}>
            Manage Product Categories
          </button>{" "}
          - Product Category Detail
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Category Detail" {...a11yProps(0)} />
              <Tab label="Subcategories" {...a11yProps(1)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards view_page">
        <CustomTabPanel value={valueTabs} index={0}>
          {/* Category Info (matches Add Category fields) */}
          <div className="section_title">
            <h2>Category Info</h2>
          </div>
          <div className="gap_p">
            <div className="vew_img v2 no_radius w_100">
              <h6>Image</h6>
              <figure>
                <img
                  src={category?.image || "/static/images/placeholder.png"}
                  alt="img"
                />
              </figure>
            </div>
            <div className="w_33">
              <h6>Title</h6>
              <p>{category?.name || "-"}</p>
            </div>
            <div className="w_33">
              <h6>Service Fee</h6>
              <p>{category?.serviceFee || "-"}</p>
            </div>
            <div className="w_33">
              <h6>Featured</h6>
              {category?.isFeatured ? (
                <p>
                  <Chip size="small" label="Featured" color="success" />
                </p>
              ) : (
                <p>Not Featured</p>
              )}
            </div>
            <div className="w_100">
              <h6>Description</h6>
              <p>{category?.description || "-"}</p>
            </div>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <SubcategoryTable categoryId={id as string} />
        </CustomTabPanel>
      </div>
    </>
  );
};

export default ViewPromotion;

// Subcategory management components (local to this page)
const SubcategoryTable = ({ categoryId }: { categoryId: string }) => {
  const { isAdd, isEdit, isDelete } = usePermission({
    label: "Manage Product Categories",
    page_permission: "isView",
  });
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] =
    useState<Category | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  // API Hooks
  const [getSubcategories, { isLoading: isLoadingSubcategories }] =
    useLazyGetCategoryQuery();
  const [addSubcategory, { isLoading: isAdding }] = useAddCategoryMutation();
  const [updateSubcategory, { isLoading: isUpdating }] =
    useUpdateCategoryMutation();
  const [deleteSubcategory, { isLoading: isDeleting }] =
    useDeleteCategoryMutation();

  // Formik for Add/Edit
  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      image: "",
      isFeatured: false,
    },
    validationSchema: subcategoryValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const subcategoryData = {
          name: values.name,
          description: values.description || undefined,
          image: values.image,
          isFeatured: values.isFeatured,
          parentId: categoryId,
          isActive: true,
        };

        let response;

        if (selectedSubcategory) {
          // Update existing subcategory
          response = await updateSubcategory({
            id: selectedSubcategory._id,
            body: subcategoryData,
          }).unwrap();
          if (response.code === 200) {
            showToast("Subcategory updated successfully");
            handleCloseEditModal();
            fetchSubcategories();
          }
        } else {
          // Add new subcategory
          response = await addSubcategory(subcategoryData).unwrap();
          if (response.code === 200) {
            showToast("Subcategory added successfully");
            handleCloseAddModal();
            resetForm();
            fetchSubcategories();
          }
        }
      } catch (error: any) {
        console.error("Error saving subcategory:", error);
        showError(
          error?.data?.message ||
            `Failed to ${selectedSubcategory ? "update" : "add"} subcategory`
        );
      }
    },
  });

  const fetchSubcategories = async (currentPage?: number) => {
    try {
      const response = await getSubcategories({
        parentId: categoryId,
        page: currentPage || page,
        limit,
      }).unwrap();
      if (response.code === 200) {
        setSubcategories(response?.data?.list || []);
        setTotalCount(response?.data?.totalCount || 0);
      }
    } catch (error: any) {
      console.error("Error fetching subcategories:", error);
      showError(error?.data?.message || "Failed to fetch subcategories");
      setSubcategories([]);
      setTotalCount(0);
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || !files[0]) return;

    const file = files[0];

    if (!file.type.includes("image")) {
      showError("This field only accepts images.");
      return;
    }

    try {
      setIsLoadingImage(true);
      const res = await UploadMedia(file);
      if (res?.code === 200) {
        const imageUrl = res.data;
        formik.setFieldValue("image", imageUrl);
        showToast("Image uploaded successfully.");
      } else {
        showError(res?.message || "Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showError("Error uploading image. Please try again.");
    } finally {
      setIsLoadingImage(false);
    }
  };

  const handleOpenAddModal = () => {
    formik.resetForm();
    setSelectedSubcategory(null);
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    formik.resetForm();
  };

  const handleOpenEditModal = (subcategory: Category) => {
    setSelectedSubcategory(subcategory);
    formik.setValues({
      name: subcategory.name || "",
      description: subcategory.description || "",
      image: subcategory.image || "",
      isFeatured: subcategory.isFeatured || false,
    });
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedSubcategory(null);
    formik.resetForm();
  };

  const handleOpenDeleteModal = (subcategory: Category) => {
    setSelectedSubcategory(subcategory);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedSubcategory(null);
  };

  const handleDeleteSubcategory = async () => {
    if (!selectedSubcategory) return;

    try {
      const response = await deleteSubcategory({
        id: selectedSubcategory?._id,
      }).unwrap();
      if (response?.code === 200) {
        showToast("Subcategory deleted successfully");
        handleCloseDeleteModal();
        fetchSubcategories();
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to delete subcategory");
    }
  };

  // Handle status toggle
  const handleStatusChange = async (subcategory: Category) => {
    try {
      const nextBlocked = !subcategory.isBlocked;
      const response = await updateSubcategory({
        id: subcategory?._id,
        body: { isBlocked: nextBlocked },
      }).unwrap();

      if (response?.code === 200) {
        showToast(
          `Category ${nextBlocked ? "deactivated" : "activated"} successfully`
        );
        // Update local state
        setSubcategories((prev) =>
          prev.map((cat) =>
            cat?._id === subcategory?._id
              ? { ...cat, isBlocked: nextBlocked }
              : cat
          )
        );
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to update category status");
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchSubcategories(newPage);
  };

  useEffect(() => {
    if (categoryId) {
      fetchSubcategories();
    }
  }, [categoryId]);

  const isLoading =
    isAdding || isUpdating || isDeleting || isLoadingSubcategories;

  return (
    <>
      {" "}
      <div className="table_header">
        <div className="left_s">
          <div className="section_title">
            <h2>Subcategories</h2>
          </div>
        </div>
        {isAdd && (
          <div className="right_s">
            <Button className="btn btn_primary" onClick={handleOpenAddModal}>
              Add Subcategory
            </Button>
          </div>
        )}
      </div>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow hover>
              {/* <TableCell>ID</TableCell> */}
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Featured</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subcategories?.length > 0 ? (
              subcategories?.map((sc) => (
                <TableRow hover key={sc._id}>
                  {/* <TableCell>{sc._id}</TableCell> */}
                  <TableCell>
                    <img
                      src={sc?.image || "/static/images/placeholder.png"}
                      alt={sc?.name}
                      style={{
                        height: 40,
                        width: 60,
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                  </TableCell>
                  <TableCell>{sc?.name || "-"}</TableCell>
                  <TableCell>
                    {" "}
                    <Tooltip
                      title={sc.description || "-"}
                      placement="top"
                      arrow
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 250,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                      >
                        {sc?.description?.length > 50
                          ? sc?.description?.substring(0, 50) + "..."
                          : sc?.description}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {sc?.isFeatured ? (
                      <Chip size="small" color="success" label="Yes" />
                    ) : (
                      <Chip size="small" label="No" />
                    )}
                  </TableCell>
                  <TableCell>
                    <SwitchToggle
                      disabled={!isEdit}
                      onChange={() => {
                        handleStatusChange(sc);
                      }}
                      checked={!sc?.isBlocked}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="table_actions v2">
                      {isEdit && (
                        <IconButton onClick={() => handleOpenEditModal(sc)}>
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                      )}
                      {isDelete && (
                        <IconButton onClick={() => handleOpenDeleteModal(sc)}>
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Delete"
                          />
                        </IconButton>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow hover>
                <TableCell colSpan={6}>
                  <Box textAlign={"center"}>No data found</Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalCount > 0 && (
        // <Box mt={3} display="flex" justifyContent="center">
        <Pagination
          module={subcategories}
          page={page}
          onPageChange={handlePageChange}
          totalPages={Math.max(1, Math.ceil(totalCount / limit))}
          setPage={setPage}
          totalCount={totalCount}
        />

        // </Box>
      )}
      <Dialog
        open={addModalOpen}
        onClose={handleCloseAddModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Subcategory</DialogTitle>
        <DialogContent>
          <div className="gap_p" style={{ marginTop: 8 }}>
            <div className="control_group w_33">
              <label htmlFor="add_subcat_logo">Image</label>
              {formik.values.image ? (
                <div className="upload_image v2 no_radius">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={formik.values.image}
                        alt="Subcategory logo"
                      />
                    </figure>
                    <IconButton
                      aria-label="Remove uploaded image"
                      onClick={() => formik.setFieldValue("image", "")}
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
              ) : (
                <label
                  className="upload_image v2 no_radius upload_image_error"
                  htmlFor="add_subcat_logo"
                >
                  <Input
                    sx={{ display: "none" }}
                    id="add_subcat_logo"
                    type="file"
                    inputProps={{ accept: "image/png,image/jpeg" }}
                    onChange={handleImageUpload}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img
                        src="/static/images/placeholder.png"
                        alt="placeholder"
                      />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                      </span>
                    </figure>
                  </span>
                  {formik.touched.image && formik.errors.image && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: "block", mt: 1.5 }}
                    >
                      {formik.errors.image}
                    </Typography>
                  )}
                </label>
              )}
            </div>
            <div className="control_group w_100">
              <label htmlFor="add_subcat_name">Name</label>
              <TextField
                id="add_subcat_name"
                fullWidth
                value={formik.values.name}
                onChange={(e) => {
                  const value = e.target.value;
                  if (isValidInput(value) && value.length <= 100) {
                    formik.setFieldValue("name", e.target.value);
                  }
                }}
                placeholder="Subcategory name"
                error={Boolean(formik.touched.name && formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
            <div className="control_group w_100">
              <label htmlFor="add_subcat_desc">Description</label>
              <TextField
                id="add_subcat_desc"
                fullWidth
                multiline
                minRows={3}
                value={formik.values.description}
                onChange={(e) => {
                  const value = e.target.value;
                  if (isValidInput(value) && value.length <= 500) {
                    formik.setFieldValue("description", e.target.value);
                  }
                }}
                placeholder="Description"
                error={Boolean(
                  formik.touched.description && formik.errors.description
                )}
                helperText={
                  formik.touched.description && formik.errors.description
                }
              />
            </div>
            <div className="control_group w_33">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.isFeatured}
                    onChange={(e) =>
                      formik.setFieldValue("isFeatured", e.target.checked)
                    }
                  />
                }
                label="Mark as Featured"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            className="btn btn_primary"
            onClick={() => formik.handleSubmit()}
            disabled={isLoading || isLoadingImage}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editModalOpen}
        onClose={handleCloseEditModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Subcategory</DialogTitle>
        <DialogContent>
          <div className="form">
            <div className="gap_p" style={{ marginTop: 8 }}>
              <div className="control_group w_33">
                <label htmlFor="edit_subcat_logo">Image</label>
                {formik.values.image ? (
                  <div className="upload_image v2 no_radius">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia
                          component="img"
                          image={formik.values.image}
                          alt="Subcategory logo"
                        />
                      </figure>
                      <IconButton
                        aria-label="Remove uploaded image"
                        onClick={() => formik.setFieldValue("image", "")}
                        size="small"
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                  </div>
                ) : (
                  <label
                    className="upload_image v2 no_radius upload_image_error"
                    htmlFor="edit_subcat_logo"
                  >
                    <Input
                      sx={{ display: "none" }}
                      id="edit_subcat_logo"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={handleImageUpload}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/placeholder.png"
                          alt="placeholder"
                        />
                        <span className="upload_img_icon">
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt="Upload"
                          />
                        </span>
                      </figure>
                    </span>
                    {formik.touched.image && formik.errors.image && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", mt: 1.5 }}
                      >
                        {formik.errors.image}
                      </Typography>
                    )}
                  </label>
                )}
              </div>
              <div className="control_group w_100">
                <label htmlFor="edit_subcat_name">Name</label>
                <TextField
                  id="edit_subcat_name"
                  fullWidth
                  value={formik.values.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isValidInput(value) && value.length <= 500) {
                      formik.setFieldValue("name", e.target.value);
                    }
                  }}
                  placeholder="Subcategory name"
                  error={Boolean(formik.touched.name && formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </div>
              <div className="control_group w_100">
                <label htmlFor="edit_subcat_desc">Description</label>
                <TextField
                  id="edit_subcat_desc"
                  fullWidth
                  multiline
                  minRows={3}
                  value={formik.values.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isValidInput(value) && value.length <= 500) {
                      formik.setFieldValue("description", e.target.value);
                    }
                  }}
                  placeholder="Description"
                  error={Boolean(
                    formik.touched.description && formik.errors.description
                  )}
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                />
              </div>
              <div className="control_group w_33">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.isFeatured}
                      onChange={(e) =>
                        formik.setFieldValue("isFeatured", e.target.checked)
                      }
                    />
                  }
                  label="Mark as Featured"
                />
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            className="btn btn_primary"
            onClick={() => formik.handleSubmit()}
            disabled={isLoading || isLoadingImage}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Subcategory</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this subcategory?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            className="btn btn_primary"
            onClick={handleDeleteSubcategory}
            disabled={isLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
